import React, { Component } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import styled from '@emotion/styled';
import { GlobalStyles } from './styles/GlobalStyles';
import { useViewportSync } from './hooks/useViewportSize';
import useLeaveGuard from './hooks/useLeaveGuard';
import lazyWithReload from './utils/lazyWithReload';
import GlobalSettings from './components/GlobalSettings';
import EnvironmentRibbon from './components/EnvironmentRibbon';
import AuthGate from './components/AuthGate';
import { InstanceProvider, useInstance } from './contexts/InstanceContext';
import { SolutionProvider } from './contexts/SolutionContext';
import { AiUnreadProvider } from './contexts/AiUnreadContext';
import { ReportEditsProvider, useReportEdits } from './components/Report/ReportEditsContext';
import { TranslationProvider, useTranslation } from './hooks/useTranslation.jsx';
import { DrawerProvider, DrawerShell } from './components/DrawerSystem';
import { DialogProvider } from './components/DialogProvider';
import ChatDrawerTab from './chat-agent/ChatDrawerTab';

// Screen Components
import DataScreen from './screens/DataScreen';
import FuturesScreen from './screens/FuturesScreen';
import HumanScreen from './screens/HumanScreen';
import CentralTable from './screens/CentralTable';
import TabletInterface from './screens/TabletInterface';
import ControlPanel from './screens/ControlPanel';
import CommentsScreen from './screens/CommentsScreen';
import WeatherScreen from './screens/WeatherScreen';
// Lazy: it pulls in leaflet, and an eager import put a DEV-only labs screen
// into every page's module graph — a missing dependency there broke the
// whole app, /ratkaisut-v2 included, rather than just /statmap.
const StatMapScreen = lazyWithReload(() => import('./screens/StatMapScreen'));
import PostItScreen from './screens/PostItScreen';
import ImpactMachine from './screens/ImpactMachine';
import ImpactPathView from './screens/ImpactPathView';
const SystemicMapScreen = lazyWithReload(() => import('./screens/SystemicMapScreen'));
const ViestitScreen = lazyWithReload(() => import('./screens/Admin/ViestitScreen'));
const InfoWallScreen = lazyWithReload(() => import('./screens/InfoWallScreen'));
const ClickerScreen = lazyWithReload(() => import('./screens/ClickerScreen'));
const InfoKysyScreen = lazyWithReload(() => import('./screens/InfoKysyScreen'));
const InfoBoardScreen = lazyWithReload(() => import('./screens/Admin/InfoBoardScreen'));
const UsersScreen = lazyWithReload(() => import('./screens/Admin/UsersScreen'));
const InstanceAccessScreen = lazyWithReload(() => import('./screens/InstanceAdmin/InstanceAccessScreen'));
// The Juurisyyt module's entry: the growing galaxy while the map is generated, the voting map after (#1021).
const RatkaisutV2Screen = lazyWithReload(() => import('./screens/systemic-v2/RatkaisutScreen'));
const PainPointsCollectScreen = lazyWithReload(() => import('./screens/PainPointsCollectScreen'));
const KuusiKenttaaScreen = lazyWithReload(() => import('./modules/kuusi_kenttaa_v1/KuusiKenttaaScreen'));
const KuusiKenttaaRoomScreen = lazyWithReload(() => import('./modules/kuusi_kenttaa_v1/KuusiKenttaaRoomScreen'));
const VipupisteFocusScreen = lazyWithReload(() => import('./modules/vipupiste_focus_v1/VipupisteFocusScreen'));
const PivotsV3Screen = lazyWithReload(() => import('./modules/pivot_points_v3/PivotsV3Screen'));
const PivotsV3TeamScreen = lazyWithReload(() => import('./modules/pivot_points_v3/PivotsV3TeamScreen'));
import ModuleRoute from './screens/ModuleRoute';
import { FORESIGHT_STEPS } from './foresight/foresightSteps';
import ImpactMapView from './impactmap/ImpactMapView';
import PainPointVoteScreen from './screens/PainPointVoteScreen';
import HomeworkScreen from './screens/HomeworkScreen';
import PivotPointsScreen from './screens/PivotPointsScreen';
import RagSourceMapScreen from './screens/RagSourceMapScreen';
import RagSourceMapV2Screen from './screens/RagSourceMapV2Screen';
import CorpusTietopohjaScreen from './screens/CorpusTietopohjaScreen';
import CorpusTietopohjaV4Screen from './screens/CorpusTietopohjaV4Screen';
import DashboardScreen from './dashboard/DashboardScreen';
import PivotPointsV2Screen from './pivotpoints/PivotPointsV2Screen';
import LensesScreen from './screens/LensesScreen';
import MoodScreen from './screens/MoodScreen';
import SWOTScreen from './screens/SWOTScreen';
import PublicWorkshopReport from './screens/PublicWorkshopReport';
import ArtifactScreen from './screens/Artifact/ArtifactScreen';
import SolutionPage from './screens/SolutionPage';
import SolutionsListPage from './screens/SolutionsListPage';
import ReportsListScreen from './screens/ReportsListScreen';
import WelcomeScreen from './screens/WelcomeScreen';
import InstanceSelectionScreen from './screens/InstanceSelectionScreen';
import MeView from './screens/MeView';
import RoleSelection from './screens/RoleSelection';
import LoginScreen from './screens/LoginScreen';
import TestKuutio from './screens/TestKuutio';
import ScenarioWelcome from './components/ScenarioWelcome';
import ScreenClient from './screens/ScreenClient';
import ScreenManagerView from './screens/ScreenManagerView';
import AdminHome from './screens/AdminHome/AdminHome';
import InstanceManagerScreen from './screens/InstanceManagerScreen';
import KnowledgeScreen from './screens/Knowledge/KnowledgeScreen';
import { ControlledSlides } from './components/Media';
import WorkshopControls from './screens/WorkshopControls';
import WorkshopOrchestrator from './screens/WorkshopOrchestrator';
const FlowBuilderScreen = lazyWithReload(() => import('./screens/FlowBuilder/FlowBuilderScreen'));
const ScriptScreen = lazyWithReload(() => import('./screens/Script/ScriptScreen'));
const ModuleFacilitationScreen = lazyWithReload(() => import('./screens/ModuleFacilitation/ModuleFacilitationScreen'));
const BookletAdminScreen = lazyWithReload(() => import('./screens/BookletAdmin/BookletAdminScreen'));
const ForesightWorkspace = lazyWithReload(() => import('./foresight/ForesightWorkspace'));
// The foresight steps' participant routes (#630): module id → route, as in their manifests.
const FORESIGHT_ROUTES = Object.fromEntries(Object.entries(FORESIGHT_STEPS).map(([id, step]) => [id, step.route]));
const FORESIGHT_PATHS = Object.values(FORESIGHT_ROUTES);

/** Old /ennakointi?instance=&step= links: the same step's user-facing route, with ?kid=. */
function ForesightRedirect() {
  const params = new URLSearchParams(window.location.search);
  const next = new URLSearchParams();
  const kid = params.get('kid') || params.get('instance');
  if (kid) next.set('kid', kid);
  if (params.get('skenaario')) next.set('skenaario', params.get('skenaario'));
  return <Navigate to={`${FORESIGHT_ROUTES[params.get('step')] || FORESIGHT_PATHS[0]}?${next}`} replace />;
}
const ScreenContentScreen = lazyWithReload(() => import('./screens/ScreenContentScreen'));
const BasicInfoScreen = lazyWithReload(() => import('./screens/InstanceAdmin/BasicInfoScreen'));
const ContentScreen = lazyWithReload(() => import('./screens/InstanceAdmin/ContentScreen'));
const ParticipantsScreen = lazyWithReload(() => import('./screens/InstanceAdmin/ParticipantsScreen'));
const DatasetsScreen = lazyWithReload(() => import('./screens/InstanceAdmin/DatasetsScreen'));
const InstancePromptsScreen = lazyWithReload(() => import('./screens/InstanceAdmin/InstancePromptsScreen'));
const ExportScreen = lazyWithReload(() => import('./screens/InstanceAdmin/ExportScreen'));
import PlayView from './screens/PlayView';
import ActivityLogScreen from './screens/ActivityLogScreen';
import ReportGenScreen from './screens/ReportGenScreen';
import PrintReport from './screens/PrintReport/PrintReport';
import PromptManagerScreen from './screens/PromptManagerScreen';
import QaDrawerTab from './components/QaSidebar/QaDrawerTab';
import QaDashboard from './qa/QaDashboardScreen';
import AiUsageDashboard from './screens/AiUsageDashboard';
// Systems-analysis views (per-instance feature-flagged — see
// specs/systems-analysis-visualisation.md and config/systemsAnalysisViews.js)
import PivotNetworkScreen from './screens/systems/PivotNetworkScreen';
const SystemsMapScreen = lazyWithReload(() => import('./screens/systems/SystemsMapScreen'));
import CausalLoopScreen from './screens/systems/CausalLoopScreen';
import ActorMapScreen from './screens/systems/ActorMapScreen';
import DemosScreen from './screens/DemosScreen';

const EducationNeedsScreen = lazyWithReload(() => import('./screens/EducationNeedsScreen'));
const PivotsV3Demo = lazyWithReload(() => import('./modules/pivot_points_v3/demo/PivotsV3Demo'));
const NegotiationLabScreen = lazyWithReload(() => import('./screens/labs/forest-negotiation/NegotiationLabScreen'));

const AppContainer = styled.div`
  display: flex;
  min-height: var(--kuutio-vh, 100vh);
  background: linear-gradient(135deg, #0a0a0a 0%, #1a1a2e 100%);

  @media print {
    background: #fff;
    min-height: auto;
    display: block;
  }
`;

const MainContent = styled.div`
  flex: 1;
  min-width: 0;
  overflow: ${props => props.$noScroll ? 'hidden' : 'auto'};
  min-height: var(--kuutio-vh, 100vh);

  @media print {
    overflow: visible;
    min-height: auto;
  }
`;

// Redirect component that converts kid → instance parameter
function FacilitatorRedirect() {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const kid = params.get('kid');
  // Convert kid to instance parameter for new screen-manager; encoded so a
  // crafted kid can't smuggle extra query params into the target (#936)
  const newSearch = kid ? `?${new URLSearchParams({ instance: kid })}` : '';
  return <Navigate to={`/screen-manager${newSearch}`} replace />;
}

// Wrapper for ControlledSlides that reads instance from URL
function ControlledSlidesScreen() {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const instanceId = params.get('kid') || params.get('instance') || params.get('instanceId');

  return (
    <div style={{ width: '100vw', height: '100vh', background: '#000' }}>
      <ControlledSlides
        instanceId={instanceId}
        showCounter={true}
        showNotes={false}
      />
    </div>
  );
}

// Separate component to use useLocation hook
function AppContent() {
  useViewportSync();
  useLeaveGuard();
  const location = useLocation();
  // A workshop whose corpus is the participants' own reporting keeps the chat off
  // (settings.chatHidden, TKI 29.10.2026): the chat would read their logs back to the room.
  const { currentInstance } = useInstance();
  const chatHidden = currentInstance?.settings?.chatHidden === true;

  // PIN-locked report viewers shouldn't see the chat tab — the chat
  // bypasses the same content the section gate is hiding. The report's
  // ReportAuthProvider fires a custom event whenever lock state changes;
  // we listen here and gate ChatDrawerTab on it. Unmounting the report
  // re-fires `locked: false`, so other routes never inherit a stale lock.
  const [reportChatLocked, setReportChatLocked] = React.useState(false);
  React.useEffect(() => {
    const handler = (e) => setReportChatLocked(!!e.detail?.locked);
    window.addEventListener('kuutio:report-chat-locked', handler);
    return () => window.removeEventListener('kuutio:report-chat-locked', handler);
  }, []);

  // On pretty share URLs (/raportti/<slug>) there are no query params, so
  // the chat would run with instanceId=null and every workshop tool would
  // come back empty — producing the "alusta ei palauttanut tietoja…"
  // apology. ReportEditsProvider already resolves the slug → instanceId;
  // reuse that single source of truth as the fallback.
  const { instanceId: reportInstanceId } = useReportEdits();

  // Extract instanceId from URL query params (instance= or legacy kid=),
  // falling back to the slug-resolved instance on pretty report URLs.
  const getInstanceIdFromUrl = () => {
    const params = new URLSearchParams(location.search);
    return params.get('instance') || params.get('kid') || reportInstanceId || null;
  };

  // Pages where GlobalSettings should NOT appear (admin pages have their own header, ImpactPath has its own status dot)
  const noGlobalSettingsPages = ['/', ...FORESIGHT_PATHS, '/info', '/info/kysy', '/admin/click', '/login', '/landing', '/instances', '/welcome', '/testikuutio', '/admin', '/admin/instances', '/admin/booklet', '/admin/screen-manager', '/admin/flow-manager', '/admin/workshop', '/admin/flow', '/admin/script', '/admin/workshop/script', '/admin/screen-content', '/admin/content', '/admin/participants', '/admin/access', '/admin/users', '/admin/datasets', '/admin/export', '/admin/play', '/admin/knowledge', '/admin/rag-intake', '/admin/activity-log', '/admin/settings', '/admin/facilitation', '/admin/prompts', '/admin/instance-prompts', '/admin/qa', '/admin/ai-usage', '/screen-manager', '/display-setup', '/impactpath', '/systeemikartta', '/muutoksen-esteet', '/ongelmat', '/juurisyyt-v2', '/ongelmakartta', '/ratkaisut-v2', '/tulevaisuustestaus', '/systemicmap', '/pain-points', '/pivotpoints', '/pivotpoints-v1', '/pivotpoints-v2', '/rag-source-map', '/rag-source-map-v2', '/corpus-workspace/tietopohja', '/corpus-workspace/tietopohja-v4', '/tilannekuva', '/lenses', '/swot', '/ratkaisut', '/report', '/raportti', '/reportgen', '/reports', '/raportit', '/print-report', '/statmap', '/weather', '/homework', '/demos', '/aineisto', '/labs/vipupisteet-tki', '/labs/metsaneuvottelu', '/kuusi-kenttaa', '/kuusi-kenttaa/tehtava', '/vipupisteet-yhteiset', '/vipupisteet-yhteiset/tehtava'];
  // Pretty share URLs (/raportti/K5-… and /report/K5-…) are children of
  // the listed /raportti and /report routes — exact match in `includes`
  // would let the cog leak onto those. Pattern-match the prefixes too.
  const isReportPath = (
    location.pathname === '/report' || location.pathname.startsWith('/report/') ||
    location.pathname === '/raportti' || location.pathname.startsWith('/raportti/')
    || location.pathname.startsWith('/artifact/')
  );
  // /me has its own profile menu top right, and the cog sat on top of the
  // drawer's chat tab there. Its slug and tab routes are children of /me.
  const isMePath = location.pathname === '/me' || location.pathname.startsWith('/me/');
  const shouldShowGlobalSettings = !noGlobalSettingsPages.includes(location.pathname)
    && !location.pathname.startsWith('/screen/')
    && !isReportPath && !isMePath;

  // Pages that should NOT scroll (full-screen displays, immersive views)
  const noScrollPages = ['/table', '/tablet', '/weather', '/statmap', '/postit', '/impact-machine', '/impactpath', '/systeemikartta', '/muutoksen-esteet', '/ongelmat', '/juurisyyt-v2', '/ongelmakartta', '/ratkaisut-v2', '/systemicmap', '/pain-points', '/impactmap', '/pivotpoints', '/pivotpoints-v1', '/rag-source-map', '/rag-source-map-v2', '/corpus-workspace/tietopohja', '/corpus-workspace/tietopohja-v4', '/tilannekuva', '/lenses', '/tunnelma', '/swot', '/ratkaisut', '/kuutio', '/slides', '/admin/workshop'];
  const noScroll = noScrollPages.includes(location.pathname) || location.pathname.startsWith('/screen/');

  // Chat agent should appear on all screens except landing/setup/admin pages.
  // Report routes (/raportti, /report) now render chat via the right-edge
  // drawer tab rail, same as other screens — the old ChatAgentContainer layout
  // has been removed. Print-specific routes stay excluded.
  const noChatAgentPages = ['/', ...FORESIGHT_PATHS, '/login', '/landing', '/pin', '/testikuutio', '/screen-manager', '/screen', '/admin', '/admin/instances', '/admin/screen-manager', '/admin/flow-manager', '/admin/workshop', '/admin/flow', '/admin/script', '/admin/workshop/script', '/admin/screen-content', '/admin/content', '/admin/participants', '/admin/access', '/admin/users', '/admin/datasets', '/admin/export', '/admin/play', '/admin/knowledge', '/admin/activity-log', '/admin/settings', '/admin/facilitation', '/admin/booklet', '/admin/prompts', '/admin/instance-prompts', '/admin/qa', '/admin/ai-usage', '/print-report', '/statmap', '/weather', '/homework', '/demos', '/labs/vipupisteet-tki', '/labs/metsaneuvottelu', '/kuusi-kenttaa', '/kuusi-kenttaa/tehtava', '/vipupisteet-yhteiset/tehtava'];
  const shouldShowChatAgent = !noChatAgentPages.includes(location.pathname) && !location.pathname.startsWith('/screen/') && !location.pathname.startsWith('/admin/') && !reportChatLocked && !chatHidden;

  // Extract current screen name from location
  const getCurrentScreen = () => {
    const path = location.pathname;
    if (path === '/impactpath') return 'ImpactPathView';
    if (path === '/impactmap') return 'ImpactMapView';
    if (path === '/postit') return 'PostItScreen';
    if (path === '/weather') return 'WeatherScreen';
    if (path === '/statmap') return 'StatMapScreen';
    if (path === '/impact-machine') return 'ImpactMachine';
    return path.replace('/', '').replace('-', ' ');
  };

  // Extract device code for chat — ONLY physical device screens (/screen/P1).
  // The ?participant= param is for impact path node ownership, NOT chat identity.
  // Chat should be one conversation per browser (localStorage userId).
  const getDeviceCodeFromUrl = () => {
    const screenMatch = location.pathname.match(/^\/screen\/([A-Z][0-9]+)$/i);
    if (screenMatch) return screenMatch[1].toUpperCase();
    return null;
  };

  return (
    <AppContainer>
      <GlobalStyles />
      <MainContent $noScroll={noScroll}>

      {/* Global Settings - shows on participant screens with admin link */}
      {shouldShowGlobalSettings && <GlobalSettings />}
      {shouldShowChatAgent && (
        <ChatDrawerTab
          instanceId={getInstanceIdFromUrl()}
          scenarioId={null}
          currentScreen={getCurrentScreen()}
          deviceCode={getDeviceCodeFromUrl()}
        />
      )}

      {/* QA Testing — registers as drawer tab when enabled */}
      <QaDrawerTab />

      <Routes>
          {/* Role Selection - Default entry point */}
          {/* Default URL = workshop PIN (iPads). Sitra admins use /login. */}
          <Route path="/" element={<ScreenClient />} />
          <Route path="/login" element={<LoginScreen />} />
          <Route path="/landing" element={<RoleSelection />} />

          {/* Setup Routes */}
          <Route path="/instances" element={<InstanceSelectionScreen />} />
          <Route path="/me" element={<MeView />} />
          <Route path="/info" element={<React.Suspense fallback={null}><InfoWallScreen /></React.Suspense>} />
          <Route path="/admin/click" element={<React.Suspense fallback={null}><ClickerScreen /></React.Suspense>} />
          <Route path="/info/kysy" element={<React.Suspense fallback={null}><InfoKysyScreen /></React.Suspense>} />
          <Route path="/admin/info" element={<React.Suspense fallback={null}><InfoBoardScreen /></React.Suspense>} />
          <Route path="/me/:slug" element={<MeView />} />
          <Route path="/me/:slug/:tab" element={<MeView />} />
          <Route path="/liity/:slug" element={<MeView join />} />
          {/* Redirect old /facilitator?kid=xxx to new /screen-manager?instance=xxx */}
          <Route path="/facilitator" element={<FacilitatorRedirect />} />
          <Route path="/testikuutio" element={<TestKuutio />} />

          {/* Display Screens */}
          <Route path="/screen/data" element={<DataScreen />} />
          <Route path="/screen/futures" element={<FuturesScreen />} />
          <Route path="/screen/human" element={<HumanScreen />} />
          <Route path="/screen/solutions" element={<ModuleRoute family="solutions" />} />
          <Route path="/screen/controlled-slides" element={<ControlledSlidesScreen />} />
          <Route path="/slides" element={<ControlledSlidesScreen />} />
          
          {/* Central Table Display */}
          <Route path="/table" element={<CentralTable />} />
          
          {/* Tablet Interface */}
          <Route path="/tablet" element={<TabletInterface />} />
          
          {/* Control Panel for Testing */}
          <Route path="/control" element={<ControlPanel />} />
          
          {/* Facilitator Comments Screen */}
          <Route path="/comments" element={<CommentsScreen />} />
          
          {/* Weather Context Screen */}
          <Route path="/weather" element={<WeatherScreen />} />

          {/* Stat.fi geographic POC */}
          <Route path="/statmap" element={<React.Suspense fallback={null}><StatMapScreen /></React.Suspense>} />

          {/* Labs POC — unlisted route, not in Screen Manager or facilitator nav */}
          <Route
            path="/labs/education-needs"
            element={
              <React.Suspense fallback={null}>
                <EducationNeedsScreen />
              </React.Suspense>
            }
          />

          {/* Demo — pivot points v3 on a TKI snapshot; unlisted, not in Screen Manager or facilitator nav */}
          <Route path="/labs/vipupisteet-tki" element={<React.Suspense fallback={null}><PivotsV3Demo /></React.Suspense>} />

          {/* Demo — the negotiation lab, forestry stakeholders as AI personas (#1395); unlisted, not in Screen Manager or facilitator nav */}
          <Route path="/labs/metsaneuvottelu" element={<React.Suspense fallback={null}><NegotiationLabScreen /></React.Suspense>} />

          {/* Demos index — unlisted, not in Screen Manager or facilitator nav */}
          <Route path="/demos" element={<DemosScreen />} />
          
          {/* Virtual Post-it Organizer */}
          <Route path="/postit" element={<PostItScreen />} />
          {/* PostIt AI retired (#1409): its clustering sent browser-written prompts through an open Claude proxy */}
          <Route path="/postit-ai" element={<Navigate to={`/postit${window.location.search}`} replace />} />
          
          {/* Impact Machine */}
          <Route path="/impact-machine" element={<ImpactMachine />} />

          {/* Impact Path - Current Implementation (PathwayRenderer V2) */}
          {/* The version the instance's chain uses — see ModuleRoute */}
          <Route path="/impactpath" element={<ModuleRoute family="impact_path" />} />
          <Route path="/systemicmap" element={<React.Suspense fallback={null}><SystemicMapScreen /></React.Suspense>} />
          <Route path="/systeemikartta" element={<React.Suspense fallback={null}><SystemicMapScreen /></React.Suspense>} />
          {/* The version the instance's chain uses — see ModuleRoute (ankkurit.v1 stepper, v2 map and matrix, #1364) */}
          <Route path="/muutoksen-esteet" element={<ModuleRoute family="ankkurit" />} />
          {/* The version the instance's chain uses — see ModuleRoute (kipupisteet.v1 dialogue, v2 map, #1326) */}
          <Route path="/ongelmat" element={<ModuleRoute family="kipupisteet" />} />
          {/* The v2 chain's routes before the terms changed (#1246); bookmarks and wall configs still point here. */}
          <Route path="/systeemikartta-v2" element={<Navigate to={`/muutoksen-esteet${window.location.search}`} replace />} />
          <Route path="/kipupisteet-v2" element={<Navigate to={`/ongelmat${window.location.search}`} replace />} />
          <Route path="/kuviteltu-tulevaisuus" element={<Navigate to={`/tulevaisuustestaus${window.location.search}`} replace />} />
          {/* The map's route before it became Ongelmakartta (#1336). */}
          <Route path="/systeemikartta-v2-map" element={<Navigate to={`/ongelmakartta${window.location.search}`} replace />} />
          {/* Juurisyyt: the chain's version, v1 when it has neither (#1374) */}
          <Route path="/juurisyyt-v2" element={<ModuleRoute family="juurisyyt" />} />
          {/* The version the instance's chain uses — see ModuleRoute (systemic_map.v2 the whole map, v3 the vipupisteet on the perimeter map, #1389) */}
          <Route path="/ongelmakartta" element={<ModuleRoute family="systemic_map" />} />
          <Route path="/ratkaisut-v2" element={<React.Suspense fallback={null}><RatkaisutV2Screen /></React.Suspense>} />
          {/* The version the instance's chain uses — see ModuleRoute (future_reimagined.v1 on Ratkaisut v2, v2 on Ratkaisut v4, #1474) */}
          <Route path="/tulevaisuustestaus" element={<ModuleRoute family="future_reimagined" />} />
          <Route path="/kuusi-kenttaa" element={<React.Suspense fallback={null}><KuusiKenttaaScreen /></React.Suspense>} />
          <Route path="/kuusi-kenttaa/tehtava" element={<React.Suspense fallback={null}><KuusiKenttaaRoomScreen /></React.Suspense>} />
          <Route path="/vipupisteiden-valinta" element={<React.Suspense fallback={null}><VipupisteFocusScreen /></React.Suspense>} />
          <Route path="/vipupisteet-yhteiset" element={<React.Suspense fallback={null}><PivotsV3Screen /></React.Suspense>} />
          <Route path="/vipupisteet-yhteiset/tehtava" element={<React.Suspense fallback={null}><PivotsV3TeamScreen /></React.Suspense>} />
          <Route path="/pain-points" element={<React.Suspense fallback={null}><PainPointsCollectScreen /></React.Suspense>} />
          <Route path="/impactmap" element={<ImpactMapView />} />
          <Route path="/kipupisteet" element={<PainPointVoteScreen />} />
          <Route path="/homework" element={<HomeworkScreen />} />

          {/* Pivot Points Visualization */}
          {/* The version the instance's chain uses — see ModuleRoute */}
          <Route path="/pivotpoints" element={<ModuleRoute family="pivot_points" />} />
          <Route path="/pivotpoints-v1" element={<PivotPointsScreen />} />
          <Route path="/pivotpoints-v2" element={<PivotPointsV2Screen />} />
          {/* Retired (#687): pivots typed or preset by hand are shown by the next module */}
          <Route path="/pivot-static" element={<Navigate to={`/lenses${window.location.search}`} replace />} />
          <Route path="/pivot-manual" element={<Navigate to={`/lenses${window.location.search}`} replace />} />
          {/* RAG Source Map — bubble map of RAG sources by type & usage, specs/rag-source-heatmap.md */}
          <Route path="/rag-source-map" element={<RagSourceMapScreen />} />
          <Route path="/rag-source-map-v2" element={<RagSourceMapV2Screen />} />
          <Route path="/corpus-workspace/tietopohja" element={<CorpusTietopohjaScreen />} />
          <Route path="/corpus-workspace/tietopohja-v4" element={<CorpusTietopohjaV4Screen />} />
          {/* Tilannekuva — live workshop dashboard, specs/workshop-dashboard.md */}
          <Route path="/tilannekuva" element={<DashboardScreen />} />
          {/* Day 2 Lenses - Analytical Perspectives */}
          <Route path="/lenses" element={<LensesScreen />} />
          <Route path="/tunnelma" element={<MoodScreen />} />
          <Route path="/swot" element={<SWOTScreen />} />
          {/* Systems-analysis views — gated per-instance by SystemsViewScaffold */}
          <Route path="/systems/pivot-network" element={<PivotNetworkScreen />} />
          <Route path="/systems/map" element={<React.Suspense fallback={null}><SystemsMapScreen /></React.Suspense>} />
          <Route path="/systems/causal-loops" element={<CausalLoopScreen />} />
          <Route path="/systems/actors" element={<ActorMapScreen />} />
          {/* The version the instance's chain uses — see ModuleRoute */}
          <Route path="/ratkaisut" element={<ModuleRoute family="solutions" />} />

          {/* Workshop Reports.
            * Public listing routes (/reports, /raportit) disabled — anyone
            * with the listing could browse every workshop's report. Reports
            * stay reachable via direct hash/id URLs (/report?kid=... and
            * /raportti?kid=...). Re-enable when we have proper access
            * gating on the list view. */}
          {/* <Route path="/reports" element={<ReportsListScreen />} /> */}
          {/* <Route path="/raportit" element={<ReportsListScreen />} /> */}
          <Route path="/report" element={<PublicWorkshopReport />} />
          <Route path="/report/:slug" element={<PublicWorkshopReport />} />
          <Route path="/raportti" element={<PublicWorkshopReport />} />
          <Route path="/raportti/:slug" element={<PublicWorkshopReport />} />
          <Route path="/artifact/:hash" element={<ArtifactScreen />} />
          <Route path="/reportgen" element={<ReportGenScreen />} />
          <Route path="/print-report" element={<PrintReport />} />
          <Route path="/solutions" element={<SolutionsListPage />} />
          <Route path="/solutions/:id" element={<SolutionPage />} />

          {/* Welcome Screen - Scenario Selection */}
          <Route path="/welcome" element={<WelcomeScreen />} />

          {/* Kuutio - Clean 2x2 Scenario Selector */}
          <Route path="/kuutio" element={<ScenarioWelcome />} />

          {/* Screen Management System */}
          <Route path="/screen" element={<ScreenClient />} />
          <Route path="/pin" element={<ScreenClient />} />
          <Route path="/screen/:code" element={<ScreenClient />} />
          <Route path="/screen-manager" element={<ScreenManagerView />} />
          {/* FlowManager is retired (#688): the module chain runs from /admin/workshop */}
          <Route path="/flow-manager" element={<Navigate to={`/admin/workshop${window.location.search}`} replace />} />
          <Route path="/admin" element={<AdminHome />} />
          <Route path="/admin/instances" element={<InstanceManagerScreen />} />
          <Route path="/admin/viestit" element={<React.Suspense fallback={null}><ViestitScreen /></React.Suspense>} />
          <Route path="/admin/access" element={<React.Suspense fallback={null}><InstanceAccessScreen /></React.Suspense>} />
          <Route path="/admin/users" element={<React.Suspense fallback={null}><UsersScreen /></React.Suspense>} />
          <Route path="/admin/knowledge" element={<KnowledgeScreen />} />
          {/* Vastaanotto retired (#797): intake is per dataset, in Aineistot → Tietopohjat */}
          <Route path="/admin/rag-intake" element={<Navigate to="/admin/knowledge?tab=datasets" replace />} />
          <Route path="/admin/screen-manager" element={<ScreenManagerView />} />
          <Route path="/admin/flow-manager" element={<Navigate to={`/admin/workshop${window.location.search}`} replace />} />
          <Route path="/admin/workshop" element={<WorkshopOrchestrator />} />
          <Route path="/admin/flow" element={<React.Suspense fallback={null}><FlowBuilderScreen /></React.Suspense>} />
          {/* Käsikirjoitus: one screen, a path per stage so its stage row stays lit (#1082) */}
          <Route path="/admin/script" element={<React.Suspense fallback={null}><ScriptScreen /></React.Suspense>} />
          <Route path="/admin/workshop/script" element={<React.Suspense fallback={null}><ScriptScreen /></React.Suspense>} />
          <Route path="/admin/facilitation" element={<React.Suspense fallback={null}><ModuleFacilitationScreen /></React.Suspense>} />
          <Route path="/admin/booklet" element={<React.Suspense fallback={null}><BookletAdminScreen /></React.Suspense>} />
          <Route path="/ennakointi" element={<ForesightRedirect />} />
          {Object.entries(FORESIGHT_ROUTES).map(([moduleId, path]) => (
            <Route key={moduleId} path={path} element={<React.Suspense fallback={null}><ForesightWorkspace moduleId={moduleId} /></React.Suspense>} />
          ))}
          <Route path="/admin/screen-content" element={<React.Suspense fallback={null}><ScreenContentScreen /></React.Suspense>} />
          <Route path="/admin/play" element={<PlayView />} />
          <Route path="/admin/activity-log" element={<ActivityLogScreen />} />
          <Route path="/admin/settings" element={<React.Suspense fallback={null}><BasicInfoScreen /></React.Suspense>} />
          <Route path="/admin/content" element={<React.Suspense fallback={null}><ContentScreen /></React.Suspense>} />
          <Route path="/admin/participants" element={<React.Suspense fallback={null}><ParticipantsScreen /></React.Suspense>} />
          <Route path="/admin/datasets" element={<React.Suspense fallback={null}><DatasetsScreen /></React.Suspense>} />
          <Route path="/admin/export" element={<React.Suspense fallback={null}><ExportScreen /></React.Suspense>} />
          <Route path="/admin/prompts" element={<PromptManagerScreen />} />
          <Route path="/admin/instance-prompts" element={<React.Suspense fallback={null}><InstancePromptsScreen /></React.Suspense>} />
          <Route path="/admin/qa" element={<QaDashboard />} />
          <Route path="/admin/ai-usage" element={<AiUsageDashboard />} />
        </Routes>
      </MainContent>
      {/* A published artifact is a stand-alone page: no chat drawer on it */}
      {!location.pathname.startsWith('/artifact/') && <DrawerShell />}
      </AppContainer>
  );
}

// ErrorBoundary — catches render crashes and shows recovery UI instead of blank page / login redirect
class AppErrorBoundary extends Component {
  state = { hasError: false, error: null };

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('🔴 App crash caught by ErrorBoundary:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <AppErrorFallback
          error={this.state.error}
          onReload={() => { this.setState({ hasError: false, error: null }); window.location.reload(); }}
        />
      );
    }
    return this.props.children;
  }
}

function AppErrorFallback({ error, onReload }) {
  const { t } = useTranslation();

  return (
    <div style={{
      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      height: '100vh', background: '#0a0a0a', color: 'white', fontFamily: 'system-ui',
    }}>
      <h2 style={{ marginBottom: 8 }}>{t('common.errorBoundaryTitle')}</h2>
      <p style={{ color: 'rgba(255,255,255,0.6)', marginBottom: 24 }}>
        {error?.message || t('common.errorBoundaryUnknown')}
      </p>
      <button
        onClick={onReload}
        style={{
          padding: '12px 24px', background: '#9C27B0', color: 'white', border: 'none',
          borderRadius: 8, fontSize: 16, cursor: 'pointer',
        }}
      >
        {t('common.errorBoundaryReload')}
      </button>
    </div>
  );
}

function App() {
  return (
    <Router>
      <EnvironmentRibbon />
      <AuthGate>
        <InstanceProvider>
          <TranslationProvider defaultLanguage="fi">
            <AppErrorBoundary>
              <DialogProvider>
                <AiUnreadProvider>
                  <SolutionProvider>
                    <ReportEditsProvider>
                      <DrawerProvider>
                        <AppContent />
                      </DrawerProvider>
                    </ReportEditsProvider>
                  </SolutionProvider>
                </AiUnreadProvider>
              </DialogProvider>
            </AppErrorBoundary>
          </TranslationProvider>
        </InstanceProvider>
      </AuthGate>
    </Router>
  );
}

export default App;